import React, { useEffect, useState } from 'react';
import { ModalX } from '../../../components';
import { Button, Checkbox, Input, Option, Select } from '@material-tailwind/react';
import { INVENTORY_CATEGORIES_ENUM } from '../../../utils/enums';
import { CATEGORY_META, UNIT_SUGGESTIONS } from '../utils';
import { useAddInventoryItem, useUpdateInventoryItem } from '../../../custom-hooks/use-inventory';

const EMPTY_FORM = {
    name: '',
    category: INVENTORY_CATEGORIES_ENUM[0],
    unitOfMeasure: '',
    reorderLevel: 0,
    dailyMaxLimit: '',
    tracksBreakage: false,
    initialStock: 0,
};

const ItemFormModal = ({ isOpen, onClose, item }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const isUpdating = !!item;

    const addMutation = useAddInventoryItem();
    const updateMutation = useUpdateInventoryItem();

    useEffect(() => {
        if (item) {
            setForm({
                name: item.name || '',
                category: item.category,
                unitOfMeasure: item.unitOfMeasure || '',
                reorderLevel: item.reorderLevel ?? 0,
                dailyMaxLimit: item.dailyMaxLimit ?? '',
                tracksBreakage: !!item.tracksBreakage,
            });
        } else if (isOpen) {
            setForm(EMPTY_FORM);
        }
    }, [item, isOpen]);

    const handleChange = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            name: form.name,
            category: form.category,
            unitOfMeasure: form.unitOfMeasure,
            reorderLevel: form.reorderLevel === '' ? 0 : Number(form.reorderLevel),
            dailyMaxLimit: form.dailyMaxLimit === '' ? null : Number(form.dailyMaxLimit),
            tracksBreakage: form.tracksBreakage,
        };

        if (isUpdating) {
            await updateMutation.mutateAsync({ itemId: item._id, data: payload });
        } else {
            await addMutation.mutateAsync({ ...payload, initialStock: Number(form.initialStock) || 0 });
        }
        onClose();
    };

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title={isUpdating ? 'Edit Item' : 'Add New Item'} maxWidth="lg">
            <form onSubmit={handleSubmit} className="space-y-3.5">
                <Input label="Item Name" value={form.name} onChange={(e) => handleChange('name', e.target.value)} required autoFocus />

                {/* Material Tailwind's Select/Input both enforce a 200px internal min-width,
                    so paired fields need real room — stack on narrow screens, row from sm+ */}
                <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="flex-1">
                        {/* `selected` computes the closed-box label ourselves — Material Tailwind's
                            own display lookup for the closed Select is unreliable in this version */}
                        <Select
                            label="Category"
                            value={form.category}
                            onChange={(v) => handleChange('category', v)}
                            selected={() => CATEGORY_META[form.category]?.label || form.category}
                        >
                            {INVENTORY_CATEGORIES_ENUM.map((cat) => (
                                <Option key={cat} value={cat}>{CATEGORY_META[cat]?.label || cat}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex-1">
                        <Input
                            label="Unit (optional, e.g. Packets)"
                            value={form.unitOfMeasure}
                            onChange={(e) => handleChange('unitOfMeasure', e.target.value)}
                            list="inv-unit-suggestions"
                        />
                    </div>
                    <datalist id="inv-unit-suggestions">
                        {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
                    </datalist>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="flex-1">
                        <Input
                            type="number"
                            label="Reorder Level"
                            value={form.reorderLevel}
                            onChange={(e) => handleChange('reorderLevel', e.target.value)}
                            min="0"
                        />
                    </div>
                    <div className="flex-1">
                        <Input
                            type="number"
                            label="Daily Max (optional)"
                            value={form.dailyMaxLimit}
                            onChange={(e) => handleChange('dailyMaxLimit', e.target.value)}
                            min="0"
                        />
                    </div>
                </div>

                {!isUpdating && (
                    <Input
                        type="number"
                        label="Initial Stock"
                        value={form.initialStock}
                        onChange={(e) => handleChange('initialStock', e.target.value)}
                        min="0"
                    />
                )}

                <Checkbox
                    label="Tracks breakage / loss (cups, glasses, tissue rolls…)"
                    checked={form.tracksBreakage}
                    onChange={(e) => handleChange('tracksBreakage', e.target.checked)}
                    className="h-4 w-4 p-0"
                    labelProps={{ className: 'text-xs' }}
                />

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="text" className="rounded-full" onClick={onClose} type="button">Cancel</Button>
                    <Button
                        type="submit"
                        className="rounded-full bg-inv-brand shadow-none hover:bg-inv-brand-dark"
                        loading={addMutation.isLoading || updateMutation.isLoading}
                    >
                        {isUpdating ? 'Save Changes' : 'Add Item'}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default ItemFormModal;
